import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import CONFIG from '../config';

const SimulationContext = createContext();

export const useSimulation = () => useContext(SimulationContext);

export const SimulationProvider = ({ children }) => {
  const [project, setProject] = useState(null);
  const [allProjects, setAllProjects] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pipeline, setPipeline] = useState({
    entry: 0,
    batch: 0,
    run: 0,
    stage: 0,
    medical: 0,
    completed: 0,
    subStages: {} // New field for project-specific sub-stages
  });

  const [alerts, setAlerts] = useState([
    { id: 1, type: 'info', message: 'System initialized. Waiting for project selection.', time: new Date().toLocaleTimeString() }
  ]);

  const [devices, setDevices] = useState([]);

  // Load all projects on mount
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const token = localStorage.getItem('token');
        const resp = await axios.get(`${CONFIG.API_URL}/api/projects`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setAllProjects(resp.data);
        if (resp.data.length > 0 && !project) {
          selectProject(resp.data[0], true);
        }
      } catch (err) {
        console.error('Failed to fetch projects:', err);
      }
    };
    fetchProjects();

    // Fetch devices
    const fetchDevices = async () => {
        try {
            const token = localStorage.getItem('token');
            const devResp = await axios.get(`${CONFIG.API_URL}/api/devices`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setDevices(devResp.data.map(d => ({
                id: d.device_id,
                type: d.type,
                status: d.status,
                lastSync: new Date(d.last_sync).toLocaleTimeString()
            })));
        } catch (err) {
            console.error('Failed to fetch devices:', err);
        }
    };
    fetchDevices();
  }, []);

  const addAlert = useCallback((message, type = 'info') => {
    // Determine if we should use a full modal or a small toast
    const isProminent = type === 'success' || type === 'error';

    if (isProminent) {
      Swal.fire({
        title: type === 'success' ? 'Success!' : 'Error',
        text: message,
        icon: type,
        confirmButtonColor: 'var(--primary)',
        timer: type === 'success' ? 2000 : undefined
      });
    } else {
      const Toast = Swal.mixin({
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true
      });
      Toast.fire({
        icon: type,
        title: message
      });
    }

    // Also keep the history in state for the Live Command Center logs
    const id = Date.now();
    setAlerts(prev => [{
      id,
      type,
      message,
      time: new Date().toLocaleTimeString()
    }, ...prev].slice(0, 50));
  }, []);

  const selectProject = useCallback(async (proj, silent = false) => {
    if (!proj) return;
    try {
      setProject(proj);
      const token = localStorage.getItem('token');
      const candResp = await axios.get(`${CONFIG.API_URL}/api/projects/${proj._id}/candidates`, { // Updated correct endpoint if needed
        headers: { Authorization: `Bearer ${token}` }
      });
      // Fallback if the above doesn't work (depending on their API structure)
      // I'll stick to the one I saw earlier: /api/candidates/${proj._id}
    } catch(e) {}
    
    try {
      setLoading(true);
      setProject(proj);
      const token = localStorage.getItem('token');
      const candResp = await axios.get(`${CONFIG.API_URL}/api/candidates/${proj._id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCandidates(candResp.data);
      
      const counts = { 
        entry: 0, 
        batch: 0, 
        run: 0, 
        stage: 0, 
        medical: 0, 
        completed: 0,
        subStages: {} 
      };

      // Initialize sub-stages from project configuration
      if (proj.stages) {
        proj.stages.forEach(s => {
          counts.subStages[s.name] = 0;
        });
      }

      candResp.data.forEach(c => {
        if (c.status === 'REJECTED' || c.status === 'COMPLETED') return;
        
        const stage = (c.current_stage || '').toUpperCase();
        
        // Map common stage names to simple keys for the dashboard
        if (stage === 'ENTRY') counts.entry++;
        else if (stage === 'BATCHING') counts.batch++;
        else if (stage === 'TRACKER') counts.run++;
        else if (stage === 'PHYSICAL_TEST') counts.stage++;
        else if (stage === 'MEDICAL') counts.medical++;
      });

      // Recalculate completed/rejected for the pipeline object if needed
      counts.completed = candResp.data.filter(c => c.status === 'COMPLETED').length;
      counts.rejected = candResp.data.filter(c => c.status === 'REJECTED').length;

      candResp.data.forEach(c => {
        if (c.status === 'REJECTED' || c.status === 'COMPLETED') return;
        if (c.current_stage.toUpperCase() === 'PHYSICAL_TEST' && c.current_sub_stage) {
          counts.subStages[c.current_sub_stage] = (counts.subStages[c.current_sub_stage] || 0) + 1;
        }
      });
      setPipeline(counts);
      if (!silent) {
        addAlert(`Switched to project: ${proj.name}`, 'success');
      }
    } catch (err) {
      console.error('Failed to load project data:', err);
      addAlert('Failed to load project candidate data.', 'error');
    } finally {
      setLoading(false);
    }
  }, [addAlert]);

  const simulateArrivals = useCallback(async (count = 10) => {
    if (!project) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${CONFIG.API_URL}/api/candidates/simulate-arrivals`, {
        projectId: project._id,
        count
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      // Refresh project data to update counts and candidates list (silently)
      await selectProject(project, true);
      addAlert(`Simulated ${count} candidate arrivals at Entry Gate.`, 'success');
    } catch (err) {
      console.error('Simulation arrival failed:', err);
      addAlert('Failed to simulate arrivals in database.', 'error');
    }
  }, [project, selectProject, addAlert]);

  const moveCandidates = useCallback(async (options) => {
    const { from, to, fromSub, toSub, count = 1 } = options;
    if (!project) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${CONFIG.API_URL}/api/candidates/move`, {
        projectId: project._id,
        fromStage: from,
        toStage: to,
        fromSubStage: fromSub,
        toSubStage: toSub,
        count
      }, { headers: { Authorization: `Bearer ${token}` } });

      // Refresh project data (silently)
      await selectProject(project, true);
      addAlert(`Successfully shifted ${count} candidate(s) to ${toSub || to.toUpperCase()}.`, 'info');
    } catch (err) {
      console.error('Simulation movement failed:', err);
      addAlert(err.response?.data?.message || 'Failed to move candidates in database.', 'error');
    }
  }, [project, selectProject, addAlert]);

  const triggerFailure = useCallback((device) => {
    setAlerts(prev => [{
      id: Date.now(),
      type: 'error',
      message: `CRITICAL: ${device} has disconnected from the server!`,
      time: new Date().toLocaleTimeString()
    }, ...prev]);
  }, []);

  const failCandidates = useCallback(async (options) => {
    const { atStage, atSubStage, count = 1 } = options;
    if (!project) return;
    try {
      const token = localStorage.getItem('token');
      const resp = await axios.post(`${CONFIG.API_URL}/api/candidates/fail`, {
        projectId: project._id,
        atStage,
        atSubStage,
        count
      }, { headers: { Authorization: `Bearer ${token}` } });
      await selectProject(project, true);
      addAlert(`❌ ${resp.data.failedCount} candidate(s) permanently failed at ${atSubStage || atStage.toUpperCase()}.`, 'warning');
    } catch (err) {
      console.error('Fail candidates failed:', err);
      addAlert(err.response?.data?.message || 'No active candidates at that stage.', 'error');
    }
  }, [project, selectProject, addAlert]);

  const resetSimulation = useCallback(async () => {
    if (!project) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${CONFIG.API_URL}/api/candidates/reset-simulation`, {
        projectId: project._id
      }, { headers: { Authorization: `Bearer ${token}` } });

      // Refresh project data (silently)
      await selectProject(project, true);
      addAlert('Simulation state reset in database.', 'info');
    } catch (err) {
      console.error('Simulation reset failed:', err);
      addAlert('Failed to reset simulation in database.', 'error');
    }
  }, [project, selectProject, addAlert]);

  return (
    <SimulationContext.Provider value={{
      project,
      setProject: selectProject,
      allProjects,
      setAllProjects,
      candidates,
      setCandidates,
      loading,
      pipeline,
      alerts,
      addAlert,
      devices,
      simulateArrivals,
      moveCandidates,
      failCandidates,
      triggerFailure,
      resetSimulation
    }}>
      {children}
    </SimulationContext.Provider>
  );
};
